import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDividerModule } from '@angular/material/divider';
import { FormsModule } from '@angular/forms';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { StateManagementService } from '../../services/state-management.service';
import { OnboardingApiService } from '../../services/onboarding-api.service';
import { environment } from '../../../environments/environment';
import { DeviceCart } from '../device-selection/device-selection.component';

export interface DevicePaymentResult {
  paymentMethodId: string;
  paymentType: 'bank';
  useForBilling: boolean;
  cart: DeviceCart;
}

@Component({
  selector: 'app-device-payment',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatCheckboxModule,
    MatSnackBarModule,
    MatDividerModule,
    FormsModule,
  ],
  templateUrl: './device-payment.component.html',
  styleUrl: './device-payment.component.scss',
})
export class DevicePaymentComponent implements OnInit {
  @Input() organizationId!: string;
  @Input() cart!: DeviceCart;
  @Output() paymentComplete = new EventEmitter<DevicePaymentResult>();
  @Output() paymentBack = new EventEmitter<void>();

  isProcessing = false;
  useForBilling = true;
  bankAccountSetupComplete = false;
  hasRecurringCharges = false;

  private stripe: Stripe | null = null;
  private paymentMethodId = '';

  get recurringChargeStatus(): string {
    if (this.cart.simFees > 0 && this.cart.paymentPlan === '12months') {
      return 'both';
    }
    if (this.cart.simFees > 0) {
      return 'sim';
    }
    if (this.cart.paymentPlan === '12months') {
      return 'devices';
    }
    return 'none';
  }

  constructor(
    public stateService: StateManagementService,
    private api: OnboardingApiService,
    private snackBar: MatSnackBar,
  ) {}

  async ngOnInit(): Promise<void> {
    this.stripe = await loadStripe(environment.stripeKey);
    // Disable billing checkbox if there are already recurring charges
    this.hasRecurringCharges = this.cart.simFees > 0 || this.cart.paymentPlan === '12months';
    // Auto-enable for billing if recurring charges exist
    if (this.hasRecurringCharges) {
      this.useForBilling = true;
    }
  }

  async processPayment(): Promise<void> {
    if (!this.stripe) {
      this.snackBar.open('Stripe is not loaded. Please try again.', 'Close', {
        duration: 4000,
      });
      return;
    }

    this.isProcessing = true;

    try {
      await this.processBankPayment();
    } catch (err: any) {
      this.snackBar.open(
        'Payment Error: ' + (err.message || err),
        'Close',
        { duration: 6000 }
      );
      console.error('Payment failed:', err);
    } finally {
      this.isProcessing = false;
    }
  }

  private async processBankPayment(): Promise<void> {
    if (!this.stripe) {
      throw new Error('Stripe not initialized');
    }

    // Step 1: Create SetupIntent for bank account
    const setupResult = await this.api
      .createDeviceOrderPayment(this.organizationId, {
        paymentType: 'bank',
        cart: this.cart,
        useForBilling: this.useForBilling,
      })
      .toPromise();

    if (!setupResult?.clientSecret || !setupResult?.setupIntentId) {
      throw new Error('Invalid response from payment setup.');
    }

    const setupIntentId = setupResult.setupIntentId;

    // Step 2: Collect bank account via Financial Connections
    const collectResult = await (this.stripe as any).collectBankAccountForSetup(
      {
        clientSecret: setupResult.clientSecret,
        params: {
          payment_method_type: 'us_bank_account',
          payment_method_data: {
            billing_details: {
              name: this.stateService.snapshot.organization?.Name || '',
              email: this.stateService.snapshot.organization?.Email || '',
            },
          },
        },
        expand: ['payment_method'],
      }
    );

    if (collectResult.error) {
      throw new Error(
        collectResult.error.message || 'Bank account collection failed.'
      );
    }

    const setupIntent = collectResult.setupIntent;
    if (!setupIntent) {
      throw new Error('Bank account setup failed: no setup intent returned');
    }

    // Handle requires_confirmation status by confirming manually
    if (setupIntent.status === 'requires_confirmation') {
      const confirmResult = await (this.stripe as any).confirmUsBankAccountSetup(
        setupResult.clientSecret
      );
      if (confirmResult.error) {
        throw new Error(
          confirmResult.error.message || 'Bank account confirmation failed.'
        );
      }
      if (confirmResult.setupIntent?.status !== 'succeeded') {
        throw new Error(
          'Bank account setup confirmation failed: ' +
          (confirmResult.setupIntent?.status || 'unknown status')
        );
      }
      this.paymentMethodId = confirmResult.setupIntent.payment_method;
    } else if (setupIntent.status === 'succeeded') {
      this.paymentMethodId = setupIntent.payment_method;
    } else {
      throw new Error(
        'Bank account setup incomplete: ' +
        (setupIntent.status || 'unknown status')
      );
    }

    this.bankAccountSetupComplete = true;

    // Step 3: Confirm device order on backend
    await this.confirmDeviceOrder(setupIntentId);

    // Step 4: Emit success immediately (don't wait for org refresh)
    this.paymentComplete.emit({
      paymentMethodId: this.paymentMethodId,
      paymentType: 'bank',
      useForBilling: this.useForBilling,
      cart: this.cart,
    });

    this.snackBar.open('Device order created successfully!', 'Close', {
      duration: 3000,
    });
  }

  private async confirmDeviceOrder(setupIntentId: string): Promise<void> {
    console.log('Confirming device order with setupIntentId:', setupIntentId);

    try {
      const result = (await Promise.race([
        this.api
          .confirmDeviceOrder(this.organizationId, {
            setupIntentId,
            cart: this.cart,
            useForBilling: this.useForBilling,
          })
          .toPromise(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Device order confirmation timeout')), 30000)
        ),
      ])) as { orderId: string; totalCharged: number; deviceCount: number };

      console.log('Device order confirmed:', result);

      if (!result?.orderId) {
        throw new Error('Device order confirmation failed.');
      }

      // Refresh organization from Firestore in background (non-blocking)
      // Don't await this - let it update state asynchronously
      this.api.getOrganization(this.organizationId).toPromise()
        .then((updatedOrg: any) => {
          if (updatedOrg) {
            console.log('Organization refreshed:', updatedOrg);
            this.stateService.setOrganization(updatedOrg);
          }
        })
        .catch((err) => {
          console.warn('Failed to refresh organization (non-critical):', err);
          // Non-fatal — state will sync on next page load
        });
    } catch (err: any) {
      console.error('confirmDeviceOrder error:', err);
      throw err;
    }
  }

  formatPrice(cents: number): string {
    return (cents / 100).toFixed(2);
  }

  goBack(): void {
    this.paymentBack.emit();
  }
}
